/**
 * post.json schema (style guide §3.2, §3.5, §3.8; spec §8 quality table). Asset-lane owned (task A7).
 * Every tuning value of the post stack lives here or in the dev panel (`PostPipeline.tuning`).
 */
import { z } from 'zod'
import type { QualityPreset } from '../contracts'
import postJson from './post.json'

const hex = z.string().regex(/^#[0-9A-Fa-f]{6}$/)
const unit = z.number().min(0).max(1)

/** Bloom resolution per tier: `full` = the finest bloom level is filtered from the full-res frame, `half` = one level coarser. */
export const BLOOM_MODES = ['full', 'half', 'off'] as const
export type BloomMode = (typeof BLOOM_MODES)[number]

/** Bloom levels (1/2 .. 1/128 of the frame). `half` drops the finest one; the widest halo is the same on every tier. */
export const BLOOM_LEVELS = 7

function dirtLayer(maxCount: number) {
  return z.object({
    count: z.number().int().min(0).max(maxCount),
    rMin: z.number().positive(),
    rMax: z.number().positive(),
    aMin: unit,
    aMax: unit,
  })
}

const rgb = z.tuple([z.number(), z.number(), z.number()])

export const gradeSchema = z.object({
  lutSize: z.number().int().min(8).max(64),
  /** LUT blend (0 = tone map only). */
  intensity: unit,
  /** Linear-light white balance per channel. */
  gain: rgb,
  /** Linear-light lift of the shadow floor per channel (tints and opens the blacks). */
  lift: z.tuple([unit, unit, unit]),
  /** Split toning (sRGB hex, luma-normalized): cool nebula shadows, warm star highlights. */
  shadowTint: hex,
  shadowAmount: unit,
  highlightTint: hex,
  highlightAmount: unit,
  saturation: z.number().min(0).max(2),
  vibrance: z.number().min(0).max(1),
  /** Display-space S-curve slope at the pivot (1 = none). */
  contrast: z.number().min(0.5).max(2),
  pivot: z.number().min(0.1).max(0.9),
})
export type GradeFile = z.infer<typeof gradeSchema>

export const tierSchema = z
  .object({
    bloom: z.enum(BLOOM_MODES),
    /** BloomNode semantics: 0 favours the tight levels, 1 the wide ones. */
    radius: unit,
    /** Lens dirt gain on the bloom (0 = off). */
    dirt: unit,
    /** Vignette strength (0 = off). */
    vignette: unit,
    /** Film grain amplitude in display units (0 = off; grade.ts). */
    grain: z.number().min(0).max(0.1),
    /** Constant radial chromatic aberration at the corners, UV units (0 = off). */
    caEdge: z.number().min(0).max(0.004),
    /** God rays (atmosphere.ts): radial-blur samples (0 = off) and resolution relative to the drawing buffer. */
    godRays: z.object({ samples: z.number().int().min(0).max(64), scale: z.number().min(0.125).max(0.5) }),
    /**
     * Screen-space AO (ao.ts): GTAO samples (0 = off), resolution relative to the drawing buffer, and the cap on its
     * internal pixels (G2): above it GTAO + denoise run at min(scale, sqrt(maxPixels / drawing pixels)) and a
     * depth-aware upsample restores full resolution; at or below it nothing changes.
     */
    ao: z.object({
      samples: z.number().int().min(0).max(32),
      scale: z.number().min(0.25).max(1),
      maxPixels: z.number().int().min(65536).max(33177600),
    }),
  })
  .refine((t) => t.bloom !== 'off' || t.dirt === 0, 'lens dirt needs bloom')

export type PostTier = z.infer<typeof tierSchema>

export const postFileSchema = z.object({
  bloom: z.object({
    /** Linear pre-tonemap luminance where bloom starts (style guide §3.5: 1.0, sunlit paint never blooms). */
    threshold: z.number().min(0),
    smoothWidth: z.number().min(0),
    strength: z.number().min(0).max(4),
    /** Per-level weights, finest first (radius mirrors them like three's BloomNode: w = mix(f, 1.2 - f, radius)). */
    levelFactors: z.array(z.number().min(0).max(1.2)).length(BLOOM_LEVELS),
    /** Tent upsample footprint in source texels. */
    upsampleSpread: z.number().min(0.25).max(3),
    /** Karis average in the first downsample (kills fireflies from sub-pixel glints). */
    karis: z.boolean(),
  }),
  tiers: z.object({ high: tierSchema, medium: tierSchema, low: tierSchema }),
  vignette: z
    .object({
      /** Aspect-corrected radius (1 = half the screen height) where darkening starts and where it is full. */
      inner: z.number().min(0),
      outer: z.number().positive(),
    })
    .refine((v) => v.outer > v.inner, 'vignette.outer must exceed inner'),
  dirt: z.object({
    seed: z.number().int().nonnegative(),
    width: z.number().int().min(64).max(2048),
    height: z.number().int().min(64).max(2048),
    /** The texture holds 0..1; the shader scales it by this before the tier's dirt gain. */
    peak: z.number().positive().max(100),
    /** Large soft grease films (radii as fractions of the width, amplitudes 0..1). */
    grease: dirtLayer(64),
    /** Bokeh-like smudge discs with a brighter rim. */
    smudges: dirtLayer(512).extend({
      rim: unit,
      /** Share of smudges that are crisp dried-droplet rings. */
      rimChance: unit,
      /** Longest major/minor ratio of a smear. */
      aspectMax: z.number().min(1).max(8),
      /** Outline irregularity (fraction of the radius). */
      wobble: unit,
      /** Edge softness range (fraction of the radius). */
      softMin: unit,
      softMax: unit,
    }),
    /** Wipe smears: `steps` soft ellipses (minor/major `aspect`) along a curved stroke of length lenMin..lenMax (fractions of the width). */
    streaks: dirtLayer(64).extend({
      steps: z.number().int().min(2).max(512),
      arc: z.number().min(0).max(Math.PI * 2),
      lenMin: z.number().positive(),
      lenMax: z.number().positive(),
      aspect: z.number().positive().max(1),
    }),
    /** Dust specks (radii in pixels). */
    specks: dirtLayer(4096),
    /** Low-frequency modulation: lattice cells across the width, minimum value. */
    noise: z.object({ cells: z.number().int().min(1).max(64), floor: unit }),
  }),
  feedback: z.object({
    /** Display-space colour of the damage edge (sRGB hex). */
    edgeColor: hex,
    edgeGain: unit,
    /** Share of the image kept under the edge tint (detail survives through the red). */
    edgeKeep: unit,
    /** Aspect-corrected radius where the edge starts and where it is full. */
    edgeInner: z.number().min(0),
    edgeOuter: z.number().positive(),
    /** Damage rise between frames that fires the chromatic aberration kick. */
    caTrigger: z.number().positive().max(1),
    /** Chromatic aberration lifetime: a hit reaction only, never longer than 0.3 s (style guide §3.8). */
    caDuration: z.number().positive().max(0.3),
    /** Radial channel offset at full kick, in UV units at the screen edge. */
    caStrength: z.number().min(0).max(0.03),
    /** Boost grade response rate (1/s). */
    boostResponse: z.number().positive(),
    boostSaturation: unit,
    boostExposure: unit,
    boostVignette: unit,
  }).refine((f) => f.edgeOuter > f.edgeInner, 'feedback.edgeOuter must exceed edgeInner'),
  /** The single tone-mapping operator (grade.ts); exposure comes from the environment (renderer.toneMappingExposure). */
  toneMapping: z.enum(['agx', 'aces', 'neutral']),
  /** Display-domain grade baked into a generated 3D LUT (grade.ts): input and output are sRGB-encoded display values. */
  grade: gradeSchema,
  /** God rays from the star (atmosphere.ts). */
  godRays: z.object({
    /** Per-sample weight decay along the ray. */
    decay: z.number().min(0.8).max(1),
    /** Rays fade with screen distance from the star: exp(-(d / length)^2), d in screen heights. */
    length: z.number().positive().max(2),
    /** Source falloff around the star (1 / UV squared): larger = tighter shafts. */
    falloff: z.number().nonnegative(),
    /** HDR luminance above which the background feeds the rays (star disc and glare). */
    threshold: z.number().nonnegative(),
    /** Source clamp. */
    clampMax: z.number().positive(),
    /** Synthetic corona behind the star (depth-masked): the light geometry carves shafts from. */
    corona: z.number().nonnegative().max(16),
    intensity: z.number().nonnegative().max(4),
    /** Rays fade out when the star is this far (UV units) past the screen edge. */
    margin: z.number().positive().max(1),
  }),
  /** Screen-space AO on indirect light only (ao.ts). */
  ao: z
    .object({
      /** View-space radius in metres (ship panels and rock boulders, not whole hulls). */
      radius: z.number().positive().max(8),
      thickness: z.number().positive().max(8),
      strength: unit,
      /** Contrast exponent on the GTAO visibility. */
      power: z.number().min(0.5).max(3),
      /** AO fades out between these view distances (metres), where its radius falls under a few pixels. */
      fadeStart: z.number().positive(),
      fadeEnd: z.number().positive(),
      /** Capped-AO upsample (G2): depth rejection sharpness on the relative view-depth difference of a low-res tap. */
      upsampleDepth: z.number().positive().max(1000),
      /** Capped-AO upsample: normal-agreement exponent of a low-res tap (0 = depth only). */
      upsampleNormal: z.number().min(0).max(64),
    })
    .refine((a) => a.fadeEnd > a.fadeStart, 'ao.fadeEnd must exceed fadeStart'),
  /** Anti-aliasing on WebGL2, where the HDR scene pass cannot use MSAA (WebGPU always uses 4× MSAA). */
  aa: z.object({ webgl2: z.enum(['smaa', 'fxaa']) }),
  /** Output dither amplitude in 8-bit steps (kills banding in dark gradients and bloom falloff). */
  dither: z.number().min(0).max(4),
})

export type PostFile = z.infer<typeof postFileSchema>

export function parsePostData(json: unknown): PostFile {
  return postFileSchema.parse(json)
}

export function loadPostData(): PostFile {
  return parsePostData(postJson)
}

export function tierOf(data: PostFile, preset: QualityPreset): PostTier {
  return data.tiers[preset]
}

